/**
 * Exchange list action/waiting flags and toolbar filter refinement (DE-9312).
 *
 * After `sqlOnlyOrderMapper` attaches display `InboundStatus` / `OutboundStatus`,
 * these helpers set `customerWating` / `ajwWaiting` / `close` / `cancel` and
 * refine rows for the Actions search-by filter. Icons use the same flags via
 * `getDerivedExchangeActionStatus` in `ActionStatusBadge.tsx`.
 */

import { isInternalActionFilterUser } from "./sqlOnlyActionFilterUtils";
import {
  NEUTRAL_COVERAGE_LABEL,
  NEUTRAL_STOCK_LABEL,
  OFF_CORE_DETAIL_LABEL,
  refreshSqlOnlyExchangeOrderStatus,
} from "./sqlOnlyOrderMapper";

export const EXCHANGE_CUSTOMER_WAITING_STATUSES = [
  "Alternate Part Not Accepted",
  "Alternate Part Accepted",
  "Accept/Decline",
  "Request Received",
  "In-stock",
  "Covered",
  NEUTRAL_STOCK_LABEL,
  NEUTRAL_COVERAGE_LABEL,
] as const;

export const EXCHANGE_AJW_WAITING_STATUSES = [
  "Alternate Offered",
  "OffCore Part Info",
  "Awaiting OffCore Info",
  OFF_CORE_DETAIL_LABEL,
] as const;

export type ExchangeActionFilterName =
  | "Action"
  | "Waiting"
  | "Open"
  | "Closed"
  | "Cancelled";

const AJW_COMPANY_ID = 2;

/** Align with `getDerivedExchangeActionStatus` — Shipped is not auto-closed for this inbound. */
export const isOffCoreDetailCompletedInbound = (
  order: Record<string, unknown>,
): boolean => {
  const inboundStatus = String(order.InboundStatus ?? "").trim();
  const inboundStatusView = String(order.InboundStatusView ?? "").trim();
  return (
    inboundStatus === OFF_CORE_DETAIL_LABEL ||
    inboundStatusView === OFF_CORE_DETAIL_LABEL ||
    inboundStatus === "OffcoreDetailCompleted" ||
    inboundStatus.toLowerCase().includes("off core detail completed") ||
    inboundStatusView.toLowerCase().includes("off core detail completed")
  );
};

/** Mutates each row in place — matches legacy `setActionStatusSqlOnly`. */
export const setExchangeActionStatusFlags = (
  rows: Array<Record<string, unknown>>,
  companyId?: number | string | null,
): void => {
  rows.forEach((item) => {
    item.close = 0;
    item.cancel = 0;
    item.customerWating = 0;
    item.ajwWaiting = 0;

    refreshSqlOnlyExchangeOrderStatus(item);

    const inboundStatus = String(item.InboundStatus ?? "");
    const outboundStatus = String(item.OutboundStatus ?? "");

    if (inboundStatus === "Cancelled" && outboundStatus === "Cancelled") {
      item.cancel = 1;
      return;
    }

    if (
      companyId != null &&
      companyId == AJW_COMPANY_ID &&
      outboundStatus === "Shipped" &&
      !isOffCoreDetailCompletedInbound(item)
    ) {
      item.close = 1;
      return;
    }

    if (
      inboundStatus === "OffCore Shipping" &&
      outboundStatus === "Shipped"
    ) {
      item.close = 1;
      return;
    }

    if (EXCHANGE_CUSTOMER_WAITING_STATUSES.includes(outboundStatus as never)) {
      item.ajwWaiting = 1;
    }
    if (EXCHANGE_AJW_WAITING_STATUSES.includes(inboundStatus as never)) {
      item.customerWating = 1;
    }
  });
};

export const rowMatchesExchangeActionFilter = (
  row: Record<string, unknown>,
  actionName: string,
  isInternal: boolean,
): boolean => {
  switch (actionName) {
    case "Cancelled":
      return row.cancel === 1;
    case "Closed":
      return row.close === 1 || row.cancel === 1;
    case "Open":
      return row.close !== 1 && row.cancel !== 1;
    case "Waiting":
      if (isInternal) {
        return row.customerWating === 1 && row.ajwWaiting === 0;
      }
      return row.customerWating === 0 && row.ajwWaiting === 1;
    case "Action":
      if (isInternal) {
        return row.ajwWaiting === 1;
      }
      return row.customerWating === 1;
    default:
      return false;
  }
};

export interface ExchangeActionFilterInput {
  name?: string;
  user?: Record<string, unknown> | null;
}

export const applyExchangeActionFilter = (
  rows: Array<Record<string, unknown>> | null | undefined,
  action: ExchangeActionFilterInput | null | undefined,
): {
  data: Array<Record<string, unknown>> | null | undefined;
  count: number;
} => {
  if (!action?.name || !rows) {
    return { data: rows, count: rows?.length ?? 0 };
  }

  const isInternal = isInternalActionFilterUser(
    action.user,
    action.user?.companyId as number | string | null | undefined,
  );

  const filteredData = rows.filter((row) =>
    rowMatchesExchangeActionFilter(row, action.name!, isInternal),
  );

  return {
    data: filteredData,
    count: filteredData.length,
  };
};
